/**
 * Parent feedback type definitions.
 *
 * Implements US-008 (T1): the IC-001 request/response contract for POST /feedback,
 * plus the five feedback categories and their display labels.
 * Mirrors the docstring conventions of types/children.ts.
 *
 * TO-LL-044 enforcement: every request body key here is EXACT camelCase matching
 * IC-001 (interface-contracts-consolidated.json) — category, message, replyToMe,
 * context.pagePath, context.appVersion, context.userAgent, context.mostRecentSessionId.
 * The category VALUE sent to the server is always the snake_case enum member
 * (e.g. 'content_problem'); the human label ('Content problem') exists on the
 * client only, via FEEDBACK_CATEGORIES below, and is never sent.
 *
 * These types are consumed by:
 * - lib/feedbackContext.ts (T1) — builds FeedbackContext
 * - services/feedback.ts (T1) — submitFeedback payload/response
 * - hooks/useSubmitFeedback.ts (T2) — mutation variable/data types
 * - components/ParentFeedbackSheet.tsx (T4) — form state and submission
 *
 * IC-001's allowlist deliberately DROPS userId, createdAt, senderName and
 * senderEmail from the request body: userId is resolved server-side from the
 * authenticated Cognito identity, createdAt is server-assigned, and the parent's
 * reply-to address is read from their own account record (FR-4.4). None of those
 * four fields appears anywhere in this file — do not add them back.
 */

/** The five feedback categories IC-001 accepts. Any other value is rejected 400. */
export type FeedbackCategory = 'bug' | 'idea' | 'content_problem' | 'billing' | 'other';

/**
 * Category value -> display label mapping. Module level `as const` array so it is
 * not rebuilt on every render of the sheet. The server only ever sees `value`
 * (the snake_case enum member); `label` is client-only display text (FR-1.3).
 */
export const FEEDBACK_CATEGORIES: readonly { value: FeedbackCategory; label: string }[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'idea', label: 'Idea' },
  { value: 'content_problem', label: 'Content problem' },
  { value: 'billing', label: 'Billing' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Auto-attached diagnostic context. Every field is OPTIONAL because every field
 * is omitted (never sent as an empty string or a placeholder) when its value is
 * unavailable — see lib/feedbackContext.ts for the per-field omission rules.
 */
export interface FeedbackContext {
  /** window.location.pathname only — never the query string or hash. Max 200 chars. */
  pagePath?: string;
  /** From getBuildId() (TS-003); omitted (not sent as 'unknown') when unresolved. Max 64 chars. */
  appVersion?: string;
  /** navigator.userAgent, max 400 chars. */
  userAgent?: string;
  /** The parent's most recent session id across all children, max 64 chars. */
  mostRecentSessionId?: string;
}

/** POST /feedback request body (IC-001). */
export interface FeedbackPayload {
  category: FeedbackCategory;
  /** 10 to 2000 characters after trimming. Enforced client-side (usability) and server-side (source of truth). */
  message: string;
  /** Defaults to true client-side (FR-1.3). */
  replyToMe: boolean;
  context: FeedbackContext;
}

/**
 * POST /feedback 201 response (IC-001). Typed for completeness only — the client
 * never renders `message` (FR-1.5 fixes the toast copy client-side) and never
 * branches behaviour on any field here, per IC-001's indistinguishability_requirement.
 */
export interface FeedbackSubmitResponse {
  feedbackId: string;
  message: string;
}
